// Catalog map — ~5k bestsellers t-SNE-projected to 2D (vectors.ArtworkMapPoint),
// rendered as a pannable/zoomable canvas scatter. Similar artworks literally
// cluster; dots are colored by category; click a dot to seed a search.
import { useEffect, useRef, useState } from "preact/hooks";
import { api } from "../../api";
import { IMG_ROOT } from "../../lib/constants";
import type { Item } from "./Grid";

interface MapPoint {
  id: number;
  x: number;
  y: number;
  title: string;
  lowResPath: string;
  categoryId: number;
}

const PALETTE = [
  "#e6553f",
  "#f2a541",
  "#ffe066",
  "#7fb069",
  "#31afa9",
  "#3f88c5",
  "#9a6fb0",
  "#d972a1",
  "#b5b8c5",
  "#8d6a4a",
  "#5bc0eb",
  "#c3f584",
];

interface CatalogMapProps {
  onSeed: (item: Item) => void;
}

export function CatalogMap({ onSeed }: CatalogMapProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const pointsRef = useRef<MapPoint[]>([]);
  const viewRef = useRef({ cx: 0.5, cy: 0.5, scale: 0.9 });
  const dragRef = useRef<{ x: number; y: number; moved: boolean } | null>(null);
  const [hover, setHover] = useState<{ p: MapPoint; sx: number; sy: number } | null>(null);
  const [status, setStatus] = useState("loading map…");

  function draw() {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const dpr = window.devicePixelRatio || 1;
    const W = wrap.clientWidth;
    const H = wrap.clientHeight;
    if (canvas.width !== W * dpr) {
      canvas.width = W * dpr;
      canvas.height = H * dpr;
    }
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);
    const { cx, cy, scale } = viewRef.current;
    const s = Math.min(W, H) * scale;
    for (const p of pointsRef.current) {
      const sx = (p.x - cx) * s + W / 2;
      const sy = (p.y - cy) * s + H / 2;
      if (sx < -4 || sy < -4 || sx > W + 4 || sy > H + 4) continue;
      ctx.fillStyle = PALETTE[p.categoryId % PALETTE.length]!;
      ctx.beginPath();
      ctx.arc(sx, sy, 2.5, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  function toScreen(p: MapPoint) {
    const wrap = wrapRef.current!;
    const W = wrap.clientWidth;
    const H = wrap.clientHeight;
    const { cx, cy, scale } = viewRef.current;
    const s = Math.min(W, H) * scale;
    return { sx: (p.x - cx) * s + W / 2, sy: (p.y - cy) * s + H / 2 };
  }

  function nearest(mx: number, my: number): MapPoint | null {
    let best: MapPoint | null = null;
    let bestD = 100; // 10px
    for (const p of pointsRef.current) {
      const { sx, sy } = toScreen(p);
      const d = (sx - mx) ** 2 + (sy - my) ** 2;
      if (d < bestD) {
        bestD = d;
        best = p;
      }
    }
    return best;
  }

  useEffect(() => {
    void (async () => {
      try {
        const res = await api.search.mapPoints();
        pointsRef.current = res.points;
        setStatus(`${res.points.length} artworks — scroll to zoom, drag to pan, click a dot`);
        draw();
      } catch (e) {
        setStatus(`⚠ map not built yet? ${e instanceof Error ? e.message : String(e)}`);
      }
    })();
    const onResize = () => draw();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <p class="ls-meta">{status}</p>
      <div
        ref={wrapRef}
        class="ls-map"
        onWheel={(e) => {
          e.preventDefault();
          const v = viewRef.current;
          v.scale = Math.min(40, Math.max(0.5, v.scale * (e.deltaY < 0 ? 1.15 : 1 / 1.15)));
          draw();
        }}
        onPointerDown={(e) => {
          dragRef.current = { x: e.clientX, y: e.clientY, moved: false };
          e.currentTarget.setPointerCapture(e.pointerId);
        }}
        onPointerMove={(e) => {
          const wrap = wrapRef.current!;
          const r = wrap.getBoundingClientRect();
          if (dragRef.current && e.buttons > 0) {
            const v = viewRef.current;
            const s = Math.min(r.width, r.height) * v.scale;
            v.cx -= (e.clientX - dragRef.current.x) / s;
            v.cy -= (e.clientY - dragRef.current.y) / s;
            if (
              Math.abs(e.clientX - dragRef.current.x) + Math.abs(e.clientY - dragRef.current.y) >
              3
            ) {
              dragRef.current.moved = true;
            }
            dragRef.current.x = e.clientX;
            dragRef.current.y = e.clientY;
            setHover(null);
            draw();
          } else {
            const p = nearest(e.clientX - r.left, e.clientY - r.top);
            if (p) {
              const { sx, sy } = toScreen(p);
              setHover({ p, sx, sy });
            } else {
              setHover(null);
            }
          }
        }}
        onPointerUp={(e) => {
          const wasDrag = dragRef.current?.moved;
          dragRef.current = null;
          if (wasDrag) return;
          const r = e.currentTarget.getBoundingClientRect();
          const p = nearest(e.clientX - r.left, e.clientY - r.top);
          if (p) {
            onSeed({ id: p.id, title: p.title, lowResPath: p.lowResPath, dist: 0 });
          }
        }}
      >
        <canvas ref={canvasRef} style="width:100%;height:100%"></canvas>
        {hover && (
          <div class="ls-map-tip" style={`left:${hover.sx + 12}px;top:${hover.sy + 12}px`}>
            <img src={`${IMG_ROOT}${hover.p.lowResPath}`} alt="" />
            <div>{hover.p.title}</div>
          </div>
        )}
      </div>
    </div>
  );
}
